/**
 * GemengApp Main Module Entry Point
 * Coordinates all municipal application modules
 */

// Shared utilities
import './shared/mobileNativeSharing.js';
import './shared/multipleStepsForm.js';
import './shared/_lightgallery.js';
import './shared/_leaflet.js';

import HallBookingModule from './hallBooking/index.js';
import { initAvailabilityNotice } from './shared/availabilityNotice.js';
import AppointmentModule from './appointment/index.js';
// import RegistrationsModule from './registrations/index.js';
import POIModule from './poi/index.js';
// import JobsModule from './jobs/index.js';

export default class GemengAppModule {
    constructor() {
        this.modules = {
            hallBooking: null,
            appointment: null,
            registrations: null,
            poi: null,
            jobs: null
        };
        
        this.init();
    }

    /**
     * Initialize all gemengApp modules
     */
    init() {
        // console.log('Initializing GemengApp Module System');

        // Un flux de disponibilité en échec doit se VOIR, sur la fiche salle comme
        // sur le formulaire. Branché AVANT les modules : l'événement part dès le
        // premier chargement de données, un abonnement plus tardif le manquerait.
        initAvailabilityNotice();

        // Initialize each module only if needed on the current page
        this.initializeModules();
        this.setupGlobalEventListeners();
        this.logActiveModules();
    }

    /**
     * Initialize individual modules
     */
    initializeModules() {
        // Hall Booking Module (reservations de salles)
        this.modules.hallBooking = HallBookingModule.createIfNeeded();

        // Appointment Module (rendez-vous)
        this.modules.appointment = AppointmentModule.createIfNeeded();
        
        // Registrations Module (inscriptions aux cours)
        // this.modules.registrations = RegistrationsModule.createIfNeeded();
        
        // POI Module (points d'intérêt)
        this.modules.poi = POIModule.createIfNeeded();
        
        // Jobs Module (liste des emplois)
        // this.modules.jobs = JobsModule.createIfNeeded();
    }

    /**
     * Setup global event listeners for cross-module communication
     */
    setupGlobalEventListeners() {
        // Listen for global gemengApp events
        document.addEventListener('gemengAppModuleReady', (e) => {
            // console.log('GemengApp module ready:', e.detail);
        });

        // Handle cross-module communication
        document.addEventListener('crossModuleCommunication', (e) => {
            this.handleCrossModuleCommunication(e.detail);
        });

        // Handle global form submissions
        document.addEventListener('gemengAppFormSubmit', (e) => {
            this.handleGlobalFormSubmit(e.detail);
        });
    }

    /**
     * Handle cross-module communication
     */
    handleCrossModuleCommunication(details) {
        const { sourceModule, targetModule, action, data } = details;
        
        // console.log(`Cross-module communication: ${sourceModule} -> ${targetModule}`, action, data);
        
        // Route communication to appropriate module
        if (this.modules[targetModule]) {
            const targetModuleInstance = this.modules[targetModule];
            if (targetModuleInstance && typeof targetModuleInstance[action] === 'function') {
                targetModuleInstance[action](data);
            }
        }
    }

    /**
     * Handle global form submissions
     */
    handleGlobalFormSubmit(details) {
        const { formType, formData, sourceModule } = details;
        
        // console.log('Global form submit:', formType, sourceModule);
        
        // Add any global form handling logic here
        // For example: analytics tracking, global validation, etc.
    }

    /**
     * Log active modules for debugging
     */
    logActiveModules() {
        const activeModules = Object.entries(this.modules)
            .filter(([name, instance]) => instance !== null)
            .map(([name]) => name);
            
        if (activeModules.length > 0) {
            // console.log('Active GemengApp modules:', activeModules.join(', '));
        } else {
            // console.log('No GemengApp modules active on this page');
        }
    }

    /**
     * Get specific module instance
     */
    getModule(moduleName) {
        return this.modules[moduleName] || null;
    }

    /**
     * Get all active modules
     */
    getActiveModules() {
        return Object.entries(this.modules)
            .filter(([name, instance]) => instance !== null)
            .reduce((acc, [name, instance]) => {
                acc[name] = instance;
                return acc;
            }, {});
    }

    /**
     * Check if any gemengApp modules are available
     */
    static isAvailable() {
        return HallBookingModule.isAvailable() ||
               AppointmentModule.isAvailable() ||
               POIModule.isAvailable();
    }

    /**
     * Factory method to create instance only if needed
     */
    static createIfNeeded() {
        if (GemengAppModule.isAvailable()) {
            return new GemengAppModule();
        }
        return null;
    }

    /**
     * Destroy all modules and clean up resources
     */
    destroy() {
        Object.values(this.modules).forEach(module => {
            if (module && typeof module.destroy === 'function') {
                module.destroy();
            }
        });
        
        this.modules = {};
        // console.log('GemengApp modules destroyed');
    }
}

// Make globally available for backward compatibility
window.GemengAppModule = GemengAppModule;
